import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useUser } from "../contexts/UserContext";
import Footer from '../components/Footer'
import { motion } from "framer-motion";
import { exportToWord } from "../utils/printUtils";
import { useReactToPrint } from 'react-to-print';
import { handleMobileFriendlyPrint, isMobileDevice } from '../utils/printHelper';
import {
  BriefcaseIcon,
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function JobApplication() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const printRef = useRef();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    companyName: "",
    position: "",
    applicationDate: "",
    address: "",
    recipientName: "",
    recipientPosition: "",
    content: "",
    language: "id",
  });

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");

  const showToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastMessage("");
      setToastType("");
    }, 3000);
  };

  useEffect(() => {
    if (userInfo?.id) {
      fetchApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      // Admin dapat melihat semua data, user biasa hanya data mereka sendiri
      let query = supabase
        .from("job_applications")
        .select("*");
      
      if (userInfo.role !== 'admin') {
        query = query.eq("user_id", userInfo.id);
      }
      
      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch user info untuk admin
      let dataWithUsers = data;
      if (userInfo.role === 'admin' && data && data.length > 0) {
        const userIds = [...new Set(data.map(item => item.user_id))];
        const { data: usersData } = await supabase
          .from("users")
          .select("id, full_name, email")
          .in("id", userIds);
        
        dataWithUsers = data.map(item => ({
          ...item,
          user: usersData?.find(u => u.id === item.user_id) || null
        }));
      }

      if (error) throw error;

      const transformed = dataWithUsers.map((item) => ({
        id: item.id,
        companyName: item.company_name,
        position: item.position,
        applicationDate: item.application_date,
        address: item.address,
        recipientName: item.recipient_name,
        recipientPosition: item.recipient_position,
        content: item.content,
        createdAt: item.created_at,
        user: item.user,
        language: item.language || "id",
      }));

      setApplications(transformed);
    } catch (error) {
      console.error("Error fetching applications:", error);
      showToast("Gagal memuat data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.companyName || !form.position || !form.applicationDate) {
      showToast("Nama perusahaan, posisi, dan tanggal wajib diisi", "error");
      return;
    }

    try {
      const payload = {
        user_id: userInfo.id,
        company_name: form.companyName,
        position: form.position,
        application_date: form.applicationDate,
        address: form.address,
        recipient_name: form.recipientName,
        recipient_position: form.recipientPosition,
        content: form.content,
        language: form.language || "id",
      };

      if (editingId) {
        const { error } = await supabase
          .from("job_applications")
          .update(payload)
          .eq("id", editingId);

        if (error) throw error;
        showToast("Berhasil mengupdate surat lamaran");
      } else {
        const { error } = await supabase
          .from("job_applications")
          .insert([payload]);

        if (error) throw error;
        showToast("Berhasil membuat surat lamaran");
      }

      fetchApplications();
      closeModal();
    } catch (error) {
      console.error("Error:", error);
      showToast(error.message || "Gagal menyimpan data", "error");
    }
  };

  const handleEdit = (application) => {
    setForm({
      companyName: application.companyName,
      position: application.position,
      applicationDate: application.applicationDate,
      address: application.address,
      recipientName: application.recipientName,
      recipientPosition: application.recipientPosition,
      content: application.content,
      language: application.language || "id",
    });
    setEditingId(application.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus surat lamaran ini?")) return;

    try {
      const { error } = await supabase
        .from("job_applications")
        .delete()
        .eq("id", id);

      if (error) throw error;

      showToast("Berhasil menghapus surat lamaran");
      fetchApplications();
    } catch (error) {
      console.error("Error:", error);
      showToast("Gagal menghapus data", "error");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({
      companyName: "",
      position: "",
      applicationDate: "",
      address: "",
      recipientName: "",
      recipientPosition: "",
      content: "",
      language: "id",
    });
  };

  const generateLetterContent = (app) => {
    if (app.content && app.content.trim()) {
      return (
        <div style={{ whiteSpace: 'pre-line' }}>
          {app.content}
        </div>
      );
    }

    const lang = app.language || 'id';
    
    // Template multi bahasa
    const templates = {
      id: {
        intro: 'Saya yang bertanda tangan di bawah ini:',
        name: 'Nama',
        address: 'Alamat',
        phone: 'Telepon',
        email: 'Email',
        para1: `Dengan ini mengajukan lamaran pekerjaan untuk mengisi posisi <strong>${app.position}</strong> di <strong>${app.companyName}</strong>.`,
        para2: `Saya adalah seorang profesional dengan pengalaman di bidang ${userInfo.job || 'teknologi informasi'}. Saya memiliki kemampuan yang kuat dalam bekerja secara tim maupun individu, serta memiliki dedikasi tinggi untuk mencapai target yang ditetapkan.`,
        para3: `Saya yakin dengan kualifikasi dan pengalaman yang saya miliki, saya dapat memberikan kontribusi positif bagi perusahaan. Saya sangat antusias untuk berkesempatan bergabung dengan tim ${app.companyName} dan siap untuk memberikan yang terbaik.`
      },
      en: {
        intro: 'I, the undersigned:',
        name: 'Name',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        para1: `I am writing to apply for the position of <strong>${app.position}</strong> at <strong>${app.companyName}</strong>.`,
        para2: `I am a professional with experience in ${userInfo.job || 'information technology'}. I have strong abilities to work in teams as well as individually, and I have a high dedication to achieving set targets.`,
        para3: `I am confident that with my qualifications and experience, I can make a positive contribution to the company. I am very enthusiastic about the opportunity to join the ${app.companyName} team and ready to give my best.`
      },
      ja: {
        intro: '私、以下の者は：',
        name: '氏名',
        address: '住所',
        phone: '電話番号',
        email: 'メール',
        para1: `貴社の<strong>${app.position}</strong>のポジションに応募させていただきます。`,
        para2: `私は${userInfo.job || '情報技術'}分野での経験を持つプロフェッショナルです。チームワークおよび個人での業務遂行能力が高く、設定された目標を達成するための強い献身性を持っています。`,
        para3: `私の資格と経験により、貴社に積極的な貢献ができると確信しております。${app.companyName}チームに参加する機会を得られることを大変楽しみにしており、最善を尽くす準備ができております。`
      },
      ko: {
        intro: '저, 아래 서명한 자는:',
        name: '이름',
        address: '주소',
        phone: '전화',
        email: '이메일',
        para1: `<strong>${app.companyName}</strong>의 <strong>${app.position}</strong> 직책에 지원하고자 합니다.`,
        para2: `저는 ${userInfo.job || '정보 기술'} 분야의 경험을 가진 전문가입니다. 팀워크와 개인 업무 수행 능력이 뛰어나며, 설정된 목표를 달성하기 위한 높은 헌신을 가지고 있습니다.`,
        para3: `제 자격과 경험으로 회사에 긍정적인 기여를 할 수 있다고 확신합니다. ${app.companyName} 팀에 합류할 기회를 얻게 되어 매우 기대되며, 최선을 다할 준비가 되어 있습니다.`
      },
      zh: {
        intro: '我，下方签署人：',
        name: '姓名',
        address: '地址',
        phone: '电话',
        email: '邮箱',
        para1: `特此申请<strong>${app.companyName}</strong>公司的<strong>${app.position}</strong>职位。`,
        para2: `我是一名在${userInfo.job || '信息技术'}领域有经验的专业人士。我具有很强的团队协作和独立工作能力，并且对实现既定目标有高度的奉献精神。`,
        para3: `我相信凭借我的资格和经验，我能够为公司做出积极的贡献。我非常热衷于有机会加入${app.companyName}团队，并准备好全力以赴。`
      }
    };

    const t = templates[lang] || templates.id;

    return (
      <div>
        <p style={{ margin: '0 0 15px 0' }}>
          {t.intro}
        </p>
        
        <div style={{ marginLeft: '0', marginBottom: '20px', lineHeight: '2' }}>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.name}</span>
            <span>: {userInfo.full_name || '-'}</span>
          </p>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.address}</span>
            <span>: {userInfo.location || '-'}</span>
          </p>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.phone}</span>
            <span>: {userInfo.phone || '-'}</span>
          </p>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.email}</span>
            <span>: {userInfo.email || '-'}</span>
          </p>
        </div>

        <p style={{ margin: '15px 0' }} dangerouslySetInnerHTML={{ __html: t.para1 }} />
        <p style={{ margin: '15px 0' }} dangerouslySetInnerHTML={{ __html: t.para2 }} />
        <p style={{ margin: '15px 0' }} dangerouslySetInnerHTML={{ __html: t.para3 }} />
      </div>
    );
  };

  const handlePreview = (app) => {
    setPreviewData(app);
    setShowPreview(true);
  };

  const handlePrint = () => {
    if (isMobileDevice()) {
      showToast('💡 Tip: Untuk hasil terbaik di mobile, gunakan Export ke Word', 'info');
      setTimeout(() => {
        handleMobileFriendlyPrint({
          documentTitle: `Surat_Lamaran_${previewData?.companyName || 'Perusahaan'}`,
          onAfterPrint: () => showToast('Dokumen berhasil dicetak')
        });
      }, 1000);
    } else {
      handleDesktopPrint();
    }
  };

  const handleDesktopPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Surat_Lamaran_${previewData?.companyName || 'Perusahaan'}`,
    onAfterPrint: () => {
      showToast('Dokumen berhasil dicetak');
    }
  });

  const handleExportWord = () => {
    exportToWord('printable-letter', `Surat_Lamaran_${previewData?.companyName || 'Perusahaan'}_${new Date().getTime()}`);
    showToast('Dokumen berhasil diexport ke Word');
  };

  // Helper untuk mendapatkan teks multi bahasa
  const getLetterTexts = (lang) => {
    const texts = {
      id: {
        title: 'SURAT LAMARAN KERJA',
        to: 'Kepada Yth.',
        defaultRecipient: 'Bapak/Ibu HRD Manager',
        subject: 'Perihal:',
        subjectText: 'Lamaran Pekerjaan sebagai',
        greeting: 'Dengan hormat,',
        closing: 'Demikian surat lamaran ini saya buat dengan sebenarnya. Besar harapan saya untuk dapat bergabung dengan perusahaan yang Bapak/Ibu pimpin. Atas perhatian dan pertimbangan Bapak/Ibu, saya ucapkan terima kasih.',
        regards: 'Hormat saya,',
        attachments: 'Lampiran:',
        attachmentList: [
          'Curriculum Vitae',
          'Fotokopi KTP',
          'Fotokopi Ijazah dan Transkrip Nilai',
          'Pas Foto terbaru (4x6)',
          'Surat Pengalaman Kerja (jika ada)'
        ]
      },
      en: {
        title: 'JOB APPLICATION LETTER',
        to: 'Dear',
        defaultRecipient: 'Sir/Madam HR Manager',
        subject: 'Subject:',
        subjectText: 'Application for the position of',
        greeting: 'Dear Sir/Madam,',
        closing: 'I have written this application letter sincerely. I very much hope to be able to join the company that you lead. I thank you for your attention and consideration.',
        regards: 'Sincerely yours,',
        attachments: 'Attachments:',
        attachmentList: [
          'Curriculum Vitae',
          'Copy of ID Card',
          'Copy of Diploma and Transcript',
          'Recent Photograph (4x6)',
          'Work Experience Certificate (if any)'
        ]
      },
      ja: {
        title: '応募書類',
        to: '拝啓',
        defaultRecipient: '人事部御中',
        subject: '件名：',
        subjectText: '応募職種：',
        greeting: '拝啓',
        closing: 'この応募書類を誠実に作成いたしました。貴社に入社できることを心より願っております。ご検討のほど、よろしくお願い申し上げます。',
        regards: '敬具',
        attachments: '添付書類：',
        attachmentList: [
          '履歴書',
          '身分証明書のコピー',
          '卒業証明書と成績証明書のコピー',
          '証明写真（4x6）',
          '職務経歴書（該当する場合）'
        ]
      },
      ko: {
        title: '입사 지원서',
        to: '귀하',
        defaultRecipient: '인사부 담당자님',
        subject: '제목:',
        subjectText: '지원 직무:',
        greeting: '귀하께,',
        closing: '이 지원서를 성실하게 작성하였습니다. 귀사에 입사할 수 있기를 진심으로 희망합니다. 관심과 검토에 감사드립니다.',
        regards: '감사합니다,',
        attachments: '첨부 서류:',
        attachmentList: [
          '이력서',
          '신분증 사본',
          '졸업증명서 및 성적증명서 사본',
          '증명사진 (4x6)',
          '경력증명서 (해당시)'
        ]
      },
      zh: {
        title: '求职信',
        to: '敬启者：',
        defaultRecipient: '人力资源经理',
        subject: '主题：',
        subjectText: '申请职位：',
        greeting: '尊敬的先生/女士：',
        closing: '本人真诚地撰写了这封求职信。非常希望能够加入贵公司。感谢您的关注和考虑。',
        regards: '此致敬礼',
        attachments: '附件：',
        attachmentList: [
          '简历',
          '身份证复印件',
          '毕业证书和成绩单复印件',
          '近期照片 (4x6)',
          '工作经历证明（如有）'
        ]
      }
    };
    return texts[lang] || texts.id;
  };

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #printable-letter,
          #printable-letter * {
            visibility: visible !important;
          }
          #printable-letter {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
        }
      `}</style>
      <div className="min-h-screen bg-white relative overflow-hidden no-print flex flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-16 h-16 relative">
                  <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 border-r-cyan-500 animate-spin"></div>
                  <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-blue-500 animate-spin-reverse"></div>
                  <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-400 to-cyan-600 animate-pulse shadow-lg"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full shadow-md animate-ping"></div>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-gray-700 font-medium text-base animate-pulse">Loading job applications...</p>
            </div>
          </div>
        ) : (
          <>
        {/* Soft Futuristic Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/30 via-cyan-100/30 to-blue-100/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-blue-100/30 via-blue-100/30 to-pink-100/30 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-50/20 via-blue-50/20 to-blue-50/20 rounded-full blur-3xl animate-float-slow"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(99, 102, 241, 0.02) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex-1">
      <div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
              <div className="relative backdrop-blur-xl bg-gradient-to-r from-blue-600/90 via-cyan-600/90 to-blue-600/90 p-6 sm:p-8 text-white rounded-t-3xl shadow-xl border border-white/20 overflow-hidden">
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer pointer-events-none"></div>
                
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 animate-pulse"></div>

                <div className="relative z-10">
                  <div className="flex justify-between items-center flex-wrap gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-4">
                      <button
                        onClick={() => navigate("/dashboard")}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all duration-200 group "
                        title="Back to Dashboard"
                      >
                        <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                      </button>
                      <div>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
                          Surat Lamaran Kerja
                        </h1>
                        <p className="text-blue-100 text-xs sm:text-sm mt-1 flex items-center gap-2">
                          <BriefcaseIcon className="w-4 h-4" />
                          <span>Buat dan kelola surat lamaran kerja</span>
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowModal(true)}
                      className="flex items-center gap-2 bg-white hover:bg-blue-50 text-blue-700 px-4 py-2.5 rounded-xl shadow-sm font-medium transition-all duration-200 "
                    >
                      <PlusIcon className="w-5 h-5" />
                      <span>Buat Baru</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white rounded-b-2xl shadow-xl p-6 sm:p-8">
                {/* Table - Desktop */}
                <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-50 to-blue-50">
                      <tr>
                        {userInfo?.role === 'admin' && (
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Dibuat Oleh</th>
                        )}
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Perusahaan</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Posisi</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tanggal</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Penerima</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {applications.length === 0 ? (
                        <tr>
                          <td colSpan={userInfo?.role === 'admin' ? "6" : "5"} className="px-4 py-8 text-center text-slate-500">
                            Belum ada surat lamaran kerja
                          </td>
                        </tr>
                      ) : (
                        applications.map((app) => (
                          <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                            {userInfo?.role === 'admin' && (
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-semibold text-sm text-slate-800">{app.user?.full_name || 'N/A'}</p>
                                  <p className="text-xs text-slate-500">{app.user?.email || ''}</p>
                                </div>
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{app.companyName}</td>
                            <td className="px-4 py-3 text-sm text-slate-700">{app.position}</td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {new Date(app.applicationDate).toLocaleDateString("id-ID")}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {app.recipientName || "-"} {app.recipientPosition && `(${app.recipientPosition})`}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handlePreview(app)}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Preview & Print"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEdit(app)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(app.id)}
                                  className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                  title="Hapus"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Cards - Mobile */}
                <div className="md:hidden space-y-4">
                  {applications.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      Belum ada surat lamaran kerja
                    </div>
                  ) : (
                    applications.map((app) => (
                      <div
                        key={app.id}
                        className="bg-gradient-to-br from-slate-50 to-blue-50 p-4 rounded-xl border border-gray-200 shadow-sm"
                      >
                        {userInfo?.role === 'admin' && app.user && (
                          <div className="pb-3 mb-3 border-b border-gray-200">
                            <p className="text-xs text-slate-500 mb-1">Dibuat Oleh:</p>
                            <p className="font-semibold text-sm text-slate-800">{app.user.full_name}</p>
                            <p className="text-xs text-slate-500">{app.user.email}</p>
                          </div>
                        )}
                        <div className="space-y-2 mb-3">
                          <h3 className="font-bold text-gray-900">{app.companyName}</h3>
                          <p className="text-sm text-slate-600">
                            <span className="font-semibold">Posisi:</span> {app.position}
                          </p>
                          <p className="text-sm text-slate-600">
                            <span className="font-semibold">Tanggal:</span>{" "}
                            {new Date(app.applicationDate).toLocaleDateString("id-ID")}
                          </p>
                          {app.recipientName && (
                            <p className="text-sm text-slate-600">
                              <span className="font-semibold">Penerima:</span> {app.recipientName}
                              {app.recipientPosition && ` (${app.recipientPosition})`}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => handlePreview(app)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 bg-green-600 hover:bg-green-700 active:scale-95 text-white rounded-lg text-xs font-bold shadow-sm hover:shadow-md transition-all min-h-[40px]"
                          >
                            <EyeIcon className="w-4 h-4" />
                            <span>Preview</span>
                          </button>
                          <button
                            onClick={() => handleEdit(app)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white rounded-lg text-xs font-bold shadow-sm hover:shadow-md transition-all min-h-[40px]"
                          >
                            <PencilIcon className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleDelete(app.id)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white rounded-lg text-xs font-bold shadow-sm hover:shadow-md transition-all min-h-[40px]"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span>Hapus</span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>

      {/* Modal */}
      {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[10000] p-4">
            <div className="bg-white w-full max-w-2xl h-auto max-h-[75vh] flex flex-col p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-2xl">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-3 sm:mb-4 flex-shrink-0">
                {editingId ? "Edit Surat Lamaran" : "Buat Surat Lamaran Baru"}
              </h2>

              <div className="flex-1 overflow-y-auto space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Nama Perusahaan <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={form.companyName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                      placeholder="PT. Contoh Indonesia"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Posisi yang Dilamar <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={form.position}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                      placeholder="Software Developer"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Tanggal Melamar <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <input
                      type="date"
                      name="applicationDate"
                      value={form.applicationDate}
                      onChange={handleChange}
                      className="w-full pl-3 sm:pl-10 pr-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 min-h-[44px] bg-white"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Alamat Perusahaan</label>
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                    placeholder="Jl. Contoh No. 123, Jakarta"
                  ></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Nama Penerima</label>
                    <input
                      type="text"
                      name="recipientName"
                      value={form.recipientName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                      placeholder="Bapak/Ibu HRD"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Jabatan Penerima</label>
                    <input
                      type="text"
                      name="recipientPosition"
                      value={form.recipientPosition}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                      placeholder="Manager HRD"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    🌐 Bahasa Surat <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="language"
                    value={form.language}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 bg-white"
                  >
                    <option value="id">🇮🇩 Bahasa Indonesia</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="ja">🇯🇵 日本語 (Japanese)</option>
                    <option value="ko">🇰🇷 한국어 (Korean)</option>
                    <option value="zh">🇨🇳 中文 (Chinese)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Pilih bahasa untuk template surat otomatis
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Isi Surat (Opsional)</label>
                  <textarea
                    name="content"
                    value={form.content}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                    placeholder="Dengan hormat, saya yang bertanda tangan di bawah ini..."
                  ></textarea>
                  <p className="text-xs text-slate-500 mt-1">
                    Jika kosong, sistem akan menggunakan template default
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl font-semibold text-slate-700 hover:bg-gray-50 active:scale-95 transition-all min-h-[48px]"
                >
                  Batal
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 active:scale-95 disabled:bg-gray-400 text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all min-h-[48px]"
                >
                  {isLoading ? "Menyimpan..." : editingId ? "Update" : "Simpan"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview & Print Modal */}
        {showPreview && previewData && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex justify-center items-center z-[10000] p-4 print:hidden">
            <div className="bg-white w-full max-w-2xl h-auto max-h-[75vh] rounded-xl sm:rounded-2xl shadow-2xl flex flex-col relative print:max-h-none print:max-w-none print:shadow-none print:rounded-none">
              {/* Header - No Print */}
              <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 print:hidden">
                <h2 className="text-xl font-bold text-slate-800 text-center sm:text-left mb-2 sm:mb-0">Preview Surat Lamaran</h2>
                
                {/* Mobile Hint */}
                <div className="block sm:hidden bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2 text-xs text-amber-800">
                  📱 <strong>Tip:</strong> Jika Print blank, gunakan <strong>Export Word</strong>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 justify-end w-full sm:w-auto">
                  <button
                    onClick={handleExportWord}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 active:scale-95 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all min-h-[44px] w-full sm:w-auto"
                    title="Export ke Word"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Export Word</span>
                  </button>
                  <button
                    onClick={handlePrint}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all min-h-[44px] w-full sm:w-auto"
                  >
                    <DocumentArrowDownIcon className="w-5 h-5" />
                    <span>Print</span>
                  </button>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2.5 border-2 border-gray-300 rounded-xl font-semibold text-slate-700 hover:bg-gray-50 active:scale-95 transition-all min-h-[44px] w-full sm:w-auto"
                  >
                    Tutup
                  </button>
                </div>
              </div>

              {/* Letter Content - Printable */}
              <div className="flex-1 overflow-y-auto p-8 print:p-0 print:overflow-visible print:h-auto">
                <div ref={printRef} className="printable-document document-container" id="printable-letter">
                  {/* A4 Paper Style */}
                  <div className="document-page">
                    {(() => {
                      const lang = previewData.language || 'id';
                      const texts = getLetterTexts(lang);
                      const locales = {
                        id: 'id-ID',
                        en: 'en-US',
                        ja: 'ja-JP',
                        ko: 'ko-KR',
                        zh: 'zh-CN'
                      };
                      const locale = locales[lang] || 'id-ID';

                      return (
                        <>
                          {/* Header Kop Surat */}
                          <div className="document-header" style={{ marginBottom: '25px' }}>
                            <h1 className="document-title" style={{ fontSize: '14pt', fontWeight: 'bold', textAlign: 'center', textDecoration: 'underline', textUnderlineOffset: '3pt', letterSpacing: '1px', marginBottom: '0' }}>
                              {texts.title}
                            </h1>
                          </div>

                          {/* Tempat dan Tanggal */}
                          <div className="document-date" style={{ textAlign: 'right', marginBottom: '25px', fontSize: '11pt' }}>
                            <p style={{ margin: 0 }}>
                              {userInfo.location || (lang === 'en' ? 'Jakarta' : lang === 'ja' ? 'ジャカルタ' : lang === 'ko' ? '자카르타' : lang === 'zh' ? '雅加达' : 'Jakarta')}, {new Date(previewData.applicationDate).toLocaleDateString(locale, { 
                                day: 'numeric',
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </p>
                          </div>

                          {/* Penerima */}
                          <div className="document-recipient" style={{ marginBottom: '25px', lineHeight: '1.6' }}>
                            <p style={{ margin: '3px 0' }}>{texts.to}</p>
                            <p style={{ margin: '3px 0', fontWeight: 'bold' }}>
                              {previewData.recipientName || texts.defaultRecipient}
                            </p>
                            {previewData.recipientPosition && (
                              <p style={{ margin: '3px 0' }}>{previewData.recipientPosition}</p>
                            )}
                            <p style={{ margin: '8px 0 3px 0', fontWeight: 'bold' }}>{previewData.companyName}</p>
                            {previewData.address && (
                              <p style={{ margin: '3px 0', whiteSpace: 'pre-line' }}>{previewData.address}</p>
                            )}
                          </div>

                          {/* Perihal */}
                          <div style={{ marginBottom: '25px' }}>
                            <p style={{ margin: 0, lineHeight: '1.6' }}>
                              <span style={{ fontWeight: 'bold' }}>{texts.subject}</span> {texts.subjectText} {previewData.position}
                            </p>
                          </div>

                          {/* Salam Pembuka */}
                          <div className="document-greeting" style={{ marginBottom: '20px' }}>
                            <p style={{ margin: 0 }}>{texts.greeting}</p>
                          </div>

                          {/* Isi Surat */}
                          <div className="document-body" style={{ marginBottom: '30px', textAlign: 'justify', lineHeight: '1.8' }}>
                            {generateLetterContent(previewData)}
                          </div>

                          {/* Penutup */}
                          <div style={{ marginBottom: '20px', textAlign: 'justify', lineHeight: '1.8' }}>
                            <p style={{ margin: '10px 0' }}>
                              {texts.closing}
                            </p>
                          </div>

                          {/* Signature */}
                          <div className="signature-section" style={{ marginTop: '40px' }}>
                            <div className="document-closing">
                              <p style={{ margin: '0 0 70px 0' }}>{texts.regards}</p>
                              <div>
                                <p style={{ margin: '5px 0', fontWeight: 'bold', textDecoration: 'underline' }}>{userInfo.full_name}</p>
                                {userInfo.phone && (
                                  <p style={{ margin: '3px 0', fontSize: '10pt' }}>{lang === 'en' ? 'Tel' : lang === 'ja' ? '電話' : lang === 'ko' ? '전화' : lang === 'zh' ? '电话' : 'Telp'}: {userInfo.phone}</p>
                                )}
                                {userInfo.email && (
                                  <p style={{ margin: '3px 0', fontSize: '10pt' }}>Email: {userInfo.email}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Lampiran */}
                          <div className="attachments" style={{ marginTop: '35px' }}>
                            <p className="attachments-title" style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '11pt' }}>{texts.attachments}</p>
                            <ol className="attachments-list" style={{ paddingLeft: '25px', lineHeight: '1.6' }}>
                              {texts.attachmentList.map((item, index) => (
                                <li key={index} style={{ margin: '5px 0' }}>{item}</li>
                              ))}
                            </ol>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toastMessage && (
          <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
            <div
              className={`px-6 py-3 rounded-lg shadow-sm ${
                toastType === "error"
                  ? "bg-rose-500 text-white"
                  : "bg-green-500 text-white"
              }`}
            >
              {toastMessage}
            </div>
          </div>
        )}
      </div>
      </main>
      <Footer />
          </>
        )}
    </div>
    </>
  );
}